import { useCallback, useEffect, useReducer, useMemo } from 'react';
import { PaginationBase as Pagination, IPaginationProps } from '@glu/data-components';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';

export type TPaginationAgGridProps = Partial<IPaginationProps>;

export const usePaginationAgGrid = () => {
  const { gridRef } = useComposedGridContext();
  const [, forceUpdate] = useReducer((i) => i + 1, 0);
  const page = gridRef.current.api.paginationGetCurrentPage();
  const pageSize = gridRef.current.api.paginationGetPageSize();
  const totalItems = gridRef.current.api.paginationGetRowCount();
  const onNextPage = useCallback(() => {
    gridRef.current.api.paginationGoToNextPage();
  }, [gridRef]);
  const onPreviousPage = useCallback(() => {
    gridRef.current.api.paginationGoToPreviousPage();
  }, [gridRef]);
  const onPageChange = useCallback(
    (newPage: number) => {
      gridRef.current.api.paginationGoToPage(newPage);
    },
    [gridRef]
  );
  const onPageSizeChange = useCallback(
    (newPageSize: number) => {
      gridRef.current.api.paginationSetPageSize(newPageSize);
    },
    [gridRef]
  );

  /**
   * Keeps ag-grid as the primary source of truth without having to duplicate state
   * The forceUpdate is unfortunate, but better than the alternative of duplicating state
   */
  useEffect(() => {
    const currRef = gridRef.current;
    const onAgGridPageChange = () => {
      forceUpdate();
    };
    if (currRef.api) {
      currRef.api.addEventListener('paginationChanged', onAgGridPageChange);
    }
    return () => {
      currRef.api.removeEventListener('paginationChanged', onAgGridPageChange);
    };
  }, [gridRef]);

  return useMemo(
    () => ({
      onNextPage,
      onPageChange,
      onPageSizeChange,
      onPreviousPage,
      page,
      pageSize,
      totalItems,
    }),
    [onNextPage, onPageChange, onPageSizeChange, onPreviousPage, page, pageSize, totalItems]
  );
};

const PaginationAgGrid = (props: TPaginationAgGridProps) => {
  const propsFromHook = usePaginationAgGrid();

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Pagination {...propsFromHook} {...props} />;
};

export default PaginationAgGrid;
